/**
 * RGM Landing page chrome icons.
 *
 * Path data is exported verbatim from Figma (`RGM  Landing Page`, file Is9E5Mleca86tD0MoDLajF);
 * only the fills are swapped for `colorConstants` so R024 stays satisfied. Grouped in one file
 * because each is a handful of lines and they are only ever used by `/home`.
 */

import { colorConstants } from 'config/colorConstants';

type IconProps = {
    className?: string;
    color?: string;
};

/**
 * "Gen Ai" mark in the hero search field. Its five-stop gradient lives on `:root` in index.css
 * so no hex literal has to appear in a `.tsx` file.
 */
export const GenAiSpark = ({ className = '' }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
        <path
            d="M12 0C18.6274 0 23.9999 5.37257 24 12C24 18.6275 18.6275 24 12 24C5.37257 23.9999 0 18.6274 0 12C5.05091e-05 5.37261 5.37261 5.05107e-05 12 0ZM12 4.7998C8.02356 4.79984 4.79984 8.02356 4.7998 12C4.7998 15.9765 8.02354 19.2002 12 19.2002C15.9765 19.2002 19.2002 15.9765 19.2002 12C19.2002 8.02354 15.9765 4.7998 12 4.7998Z"
            fill="url(#landingGenAiGradient)"
        />
        <defs>
            <linearGradient id="landingGenAiGradient" x1="20" y1="1.5" x2="3" y2="22" gradientUnits="userSpaceOnUse">
                <stop stopColor="var(--landing-genai-1)" />
                <stop offset="0.211403" stopColor="var(--landing-genai-2)" />
                <stop offset="0.472162" stopColor="var(--landing-genai-3)" />
                <stop offset="0.737827" stopColor="var(--landing-genai-4)" />
                <stop offset="0.918722" stopColor="var(--landing-genai-5)" />
            </linearGradient>
        </defs>
    </svg>
);

/** Trailing chevron on "View more" buttons and sub-route pills. */
export const LandingChevronRight = ({ className = '', color = 'currentColor' }: IconProps) => (
    <svg viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
        <path
            d="M3.10986 1.98486C3.25631 1.83842 3.49369 1.83842 3.64014 1.98486L5.89014 4.23486C6.03658 4.38131 6.03658 4.61869 5.89014 4.76514L3.64014 7.01514C3.49369 7.16158 3.25631 7.16158 3.10986 7.01514C2.96342 6.86869 2.96342 6.63131 3.10986 6.48486L5.09473 4.5L3.10986 2.51514C2.96342 2.36869 2.96342 2.13131 3.10986 1.98486Z"
            fill={color}
        />
    </svg>
);

/**
 * Delta arrow in an Executive Summary trend chip. Figma draws both states from the single
 * `trend-down-01` path and mirrors it vertically for a rise, so the two arrows stay symmetric.
 */
export const LandingTrendArrow = ({
    className = '',
    color = 'currentColor',
    isUp = false,
}: IconProps & { isUp?: boolean }) => (
    <svg
        viewBox="0 0 9 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${isUp ? '-scale-y-100' : ''} ${className}`}
        aria-hidden
    >
        <path
            d="M0.484863 2.35986C0.63131 2.21342 0.86869 2.21342 1.01514 2.35986L2.84106 4.18542C2.91969 4.26405 2.9629 4.30718 2.99634 4.33557C2.99753 4.33658 2.99892 4.33723 3 4.33813C3.00108 4.33723 3.00247 4.33658 3.00366 4.33557C3.0371 4.30718 3.08031 4.26405 3.15894 4.18542L4.18542 3.15894C4.2553 3.08906 4.32353 3.02059 4.38574 2.96777C4.45131 2.91211 4.53455 2.85219 4.64319 2.81689C4.77498 2.77407 4.91567 2.76904 5.04968 2.80115L5.10681 2.81689L5.18372 2.84766C5.25598 2.88198 5.3151 2.92604 5.36426 2.96777C5.42647 3.02059 5.4947 3.08906 5.56458 3.15894L7.875 5.46936V3.75C7.875 3.54289 8.04289 3.375 8.25 3.375C8.45711 3.375 8.625 3.54289 8.625 3.75V6.375C8.625 6.58211 8.45711 6.75 8.25 6.75H5.625C5.41789 6.75 5.25 6.58211 5.25 6.375C5.25 6.16789 5.41789 6 5.625 6H7.34473L5.03394 3.68957C4.95531 3.61095 4.9121 3.56782 4.87866 3.53943C4.87741 3.53836 4.87614 3.53745 4.875 3.5365C4.87386 3.53745 4.87259 3.53836 4.87134 3.53943C4.8379 3.56782 4.79469 3.61095 4.71606 3.68957L3.68958 4.71606C3.6197 4.78594 3.55147 4.85441 3.48926 4.90723C3.42369 4.96289 3.34045 5.0228 3.23181 5.05811C3.08118 5.10705 2.91882 5.10705 2.76819 5.05811C2.65955 5.0228 2.57631 4.96289 2.51074 4.90723C2.44853 4.85441 2.3803 4.78594 2.31042 4.71606L0.484863 2.89014C0.338417 2.74369 0.338417 2.50631 0.484863 2.35986Z"
            fill={color}
        />
    </svg>
);

/** Trailing arrow on a Recent activities row. Decorative — the rows are not clickable yet. */
export const LandingActivityArrow = ({ className = '', color = colorConstants.navV2TextMuted }: IconProps) => (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
        <path
            d="M3.33203 7.9987H12.6654M7.9987 3.33203L12.6654 7.9987L7.9987 12.6654"
            stroke={color}
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
